import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	Collapse,
	Divider,
	InputAdornment,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Toolbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useTranslation } from "react-i18next";

const HEADER_FOOTER_HEIGHT = 52;
const MIN_COLUMN_WIDTH = 80;
const EXPAND_COLUMN_WIDTH = 56;
const CHECKBOX_COLUMN_WIDTH = 56;

const isDateLikeField = (field = "", type) => {
	if (type === "date" || type === "dateTime") return true;
	const f = String(field).toLowerCase();
	return f.includes("date") || f.endsWith("_at") || f.endsWith("_on");
};

const dateSortComparator = (v1, v2) => {
	const t1 = v1 ? new Date(v1).getTime() : 0;
	const t2 = v2 ? new Date(v2).getTime() : 0;
	return t1 - t2;
};

const normalizeSortDirection = (sort) => (sort === "asc" || sort === "desc" ? sort : "asc");

const ExpandableDataGrid = ({
	data,
	columns,
	onSelect,
	customButtons,
	onPageChange,
	rowIdField,
	sortBy,
	pageSize = 10,
	showSearchBar = true,
	checkboxSelection = false,
	rowClick = false,
	onRowClick,
	pageSizeOptions,
	disableSelectAll = false,
	rowSelectionModel,
	keepNonExistentSelected = false,
	autoHeight = true,
	gridHeight = 520,
	handleSearch,
	masterDetails,
	allowMultipleExpand = true,
}) => {
	const { t } = useTranslation();
	const [searchText, setSearchText] = useState("");
	const [searchedData, setSearchedData] = useState(data || []);
	const [filteredData, setFilteredData] = useState(data || []);
	const [columnFilters, setColumnFilters] = useState({});
	const [filterAnchorEl, setFilterAnchorEl] = useState(null);
	const [activeFilterField, setActiveFilterField] = useState(null);
	const [filterOptionSearch, setFilterOptionSearch] = useState("");
	const [expandedRows, setExpandedRows] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(Number.isFinite(Number(pageSize)) ? Number(pageSize) : 10);
	const [internalSelection, setInternalSelection] = useState([]);
	const [columnWidths, setColumnWidths] = useState({});
	const [activeResizeField, setActiveResizeField] = useState(null);
	const [tableContainerWidth, setTableContainerWidth] = useState(0);
	const headerCellRefs = useRef({});
	const tableContainerRef = useRef(null);

	const normalizedPageSize = Number.isFinite(Number(pageSize)) ? Number(pageSize) : 10;
	const normalizedPageSizeOptions = useMemo(() => {
		const fallback = [5, 10, 25, 50];
		const base = Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 ? pageSizeOptions : fallback;
		const options = new Set(base.map((val) => Number(val)).filter((val) => Number.isFinite(val) && val > 0));
		options.add(normalizedPageSize);
		return Array.from(options).sort((a, b) => a - b);
	}, [pageSizeOptions, normalizedPageSize]);

	const [sortState, setSortState] = useState(() => {
		const firstSort = Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : null;
		return {
			field: firstSort?.field || null,
			direction: normalizeSortDirection(firstSort?.sort),
		};
	});

	useEffect(() => {
		setRowsPerPage(normalizedPageSize);
	}, [normalizedPageSize]);

	useEffect(() => {
		if (!tableContainerRef.current || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver((entries) => {
			const entry = entries?.[0];
			const nextWidth = entry?.contentRect?.width || 0;
			setTableContainerWidth(nextWidth);
		});
		observer.observe(tableContainerRef.current);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const firstSort = Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : null;
		setSortState({
			field: firstSort?.field || null,
			direction: normalizeSortDirection(firstSort?.sort),
		});
	}, [sortBy]);

	const enhancedColumns = useMemo(
		() =>
			(columns || []).map((col) => {
				if (!col || col.sortComparator) return col;
				if (isDateLikeField(col.field, col.type)) {
					return { ...col, sortComparator: dateSortComparator };
				}
				return col;
			}),
		[columns]
	);

	const getRowId = (row) => row?.[rowIdField];

	const stripHtml = (html = "") =>
		String(html)
			.replace(/<style[\s\S]*?<\/style>/gi, " ")
			.replace(/<script[\s\S]*?<\/script>/gi, " ")
			.replace(/<[^>]*>/g, " ")
			.replace(/&nbsp;/g, " ")
			.replace(/\s+/g, " ")
			.trim();

	const extractTextFromReactNode = (node) => {
		if (node == null || typeof node === "boolean") return "";
		if (typeof node === "string" || typeof node === "number") return String(node);
		if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(" ");

		if (typeof node === "object" && node.props) {
			const html = node.props?.dangerouslySetInnerHTML?.__html;
			if (html) return stripHtml(html);

			const candidates = [
				node.props.label,
				node.props.text,
				node.props.name,
				node.props.title,
				node.props.value,
			]
				.map(extractTextFromReactNode)
				.filter(Boolean);

			if (candidates.length) return candidates.join(" ");
			return extractTextFromReactNode(node.props.children);
		}

		return "";
	};

	const callValueFormatterSafely = (col, params, value) => {
		if (typeof col.valueFormatter !== "function") return null;

		try {
			const out1 = col.valueFormatter({ ...params, value });
			if (out1 != null && out1 !== "") return String(out1);
		} catch { }

		try {
			const out2 = col.valueFormatter(value);
			if (out2 != null && out2 !== "") return String(out2);
		} catch { }

		return null;
	};

	const getCellValueParams = (row, col) => {
		const rawValue = row?.[col.field];
		return {
			id: getRowId(row),
			field: col.field,
			row,
			value: rawValue,
		};
	};

	const getResolvedValue = (row, col) => {
		if (!col?.field) return "";
		const params = getCellValueParams(row, col);
		const rawValue = row?.[col.field];
		if (typeof col.valueGetter === "function") {
			try {
				return col.valueGetter(params);
			} catch {
				return rawValue;
			}
		}
		return rawValue;
	};

	const getDisplayedCellText = (row, col) => {
		if (!col?.field) return "";
		const params = getCellValueParams(row, col);
		const resolvedValue = getResolvedValue(row, col);

		if (typeof col.getSearchValue === "function") {
			try {
				return String(col.getSearchValue(params) ?? "");
			} catch {
				return "";
			}
		}

		if (typeof col.renderCell === "function") {
			try {
				const rendered = col.renderCell({ ...params, value: resolvedValue });
				const text = extractTextFromReactNode(rendered);
				if (text) return text;
			} catch {
				// no-op
			}
		}

		const formatted = callValueFormatterSafely(col, params, resolvedValue);
		if (formatted != null) return formatted;

		return resolvedValue == null ? "" : String(resolvedValue);
	};

	const defaultFilter = (query, rows, cols) => {
		const q = (query || "").toLowerCase().trim();
		if (!q) return rows;

		return (rows || []).filter((row) =>
			(cols || []).some((col) => {
				if (!col?.field) return false;
				if (col.searchable === false) return false;
				return getDisplayedCellText(row, col).toLowerCase().includes(q);
			})
		);
	};

	const runSearch = (query, rows) => {
		const q = (query || "").toLowerCase();

		if (typeof handleSearch === "function") {
			try {
				const result = handleSearch({ query: q, data: rows, columns: enhancedColumns });
				if (Array.isArray(result)) {
					setSearchedData(result);
					return;
				}
				if (result && typeof result.then === "function") {
					result
						.then((nextRows) => {
							if (Array.isArray(nextRows)) setSearchedData(nextRows);
						})
						.catch(() => setSearchedData([]));
					return;
				}
				return;
			} catch {
				setSearchedData([]);
				return;
			}
		}

		setSearchedData(defaultFilter(query, rows, enhancedColumns));
	};

	const getColumnByField = (field) => enhancedColumns.find((col) => col?.field === field);

	const isColumnFilterable = (col) =>
		Boolean(col?.field) &&
		col.filterable === true &&
		col.type !== "actions" &&
		col.field !== "actions";

	const getRowFilterText = (row, field) => {
		const col = getColumnByField(field);
		if (!col) return "";
		return getDisplayedCellText(row, col);
	};

	const applyColumnFilters = (rows, filters) => {
		const filterEntries = Object.entries(filters || {}).filter(([, values]) => Array.isArray(values));
		if (!filterEntries.length) return rows || [];
		if (filterEntries.some(([, values]) => values.length === 0)) return [];
		const activeFilters = filterEntries.filter(([, values]) => values.length > 0);
		if (!activeFilters.length) return rows || [];
		return (rows || []).filter((row) =>
			activeFilters.every(([field, selectedValues]) => {
				const value = getRowFilterText(row, field);
				return selectedValues.includes(value);
			})
		);
	};

	useEffect(() => {
		runSearch(searchText, data || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, searchText, enhancedColumns, handleSearch]);

	useEffect(() => {
		setFilteredData(applyColumnFilters(searchedData || [], columnFilters));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchedData, columnFilters, enhancedColumns]);

	useEffect(() => {
		setColumnFilters((prev) => {
			const validFields = new Set(enhancedColumns.filter(isColumnFilterable).map((col) => col.field));
			let changed = false;
			const next = {};
			Object.entries(prev || {}).forEach(([field, values]) => {
				if (!validFields.has(field)) {
					changed = true;
					return;
				}
				if (!Array.isArray(values)) {
					changed = true;
					return;
				}
				next[field] = values;
			});
			return changed ? next : prev;
		});
	}, [enhancedColumns]);

	useEffect(() => {
		setPage(0);
	}, [searchText, rowsPerPage, columnFilters]);

	useEffect(() => {
		const rowIds = new Set((filteredData || []).map((row) => getRowId(row)));
		setExpandedRows((prev) => prev.filter((id) => rowIds.has(id)));
	}, [filteredData]);

	const currentSelection = useMemo(() => {
		if (Array.isArray(rowSelectionModel)) return rowSelectionModel;
		if (rowSelectionModel instanceof Set) return Array.from(rowSelectionModel);
		if (rowSelectionModel?.ids instanceof Set) return Array.from(rowSelectionModel.ids);
		return internalSelection;
	}, [rowSelectionModel, internalSelection]);

	useEffect(() => {
		if (keepNonExistentSelected) return;
		const idsInData = new Set((filteredData || []).map((row) => getRowId(row)));
		const nextSelection = currentSelection.filter((id) => idsInData.has(id));
		if (nextSelection.length !== currentSelection.length) {
			if (!Array.isArray(rowSelectionModel)) setInternalSelection(nextSelection);
			if (onSelect) onSelect(nextSelection);
		}
	}, [currentSelection, filteredData, keepNonExistentSelected, onSelect, rowSelectionModel]);

	const sortedData = useMemo(() => {
		if (!sortState.field) return filteredData || [];
		const targetCol = enhancedColumns.find((col) => col.field === sortState.field);
		if (!targetCol) return filteredData || [];

		const directionFactor = sortState.direction === "desc" ? -1 : 1;
		const rows = [...(filteredData || [])];

		rows.sort((a, b) => {
			const va = getResolvedValue(a, targetCol);
			const vb = getResolvedValue(b, targetCol);

			if (typeof targetCol.sortComparator === "function") {
				return targetCol.sortComparator(va, vb) * directionFactor;
			}

			if (va == null && vb == null) return 0;
			if (va == null) return -1 * directionFactor;
			if (vb == null) return 1 * directionFactor;

			if (typeof va === "number" && typeof vb === "number") {
				return (va - vb) * directionFactor;
			}

			return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * directionFactor;
		});

		return rows;
	}, [filteredData, enhancedColumns, sortState]);

	const paginatedRows = useMemo(() => {
		const start = page * rowsPerPage;
		return sortedData.slice(start, start + rowsPerPage);
	}, [sortedData, page, rowsPerPage]);

	useEffect(() => {
		const maxPage = Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1);
		if (page > maxPage) setPage(maxPage);
	}, [sortedData.length, rowsPerPage, page]);

	const hasMasterDetails = typeof masterDetails === "function";
	const visibleColumnCount =
		enhancedColumns.length + (checkboxSelection ? 1 : 0) + (hasMasterDetails ? 1 : 0);

	const handleSortClick = (field, sortable = true) => {
		if (!sortable || !field) return;
		setSortState((prev) => {
			if (prev.field !== field) return { field, direction: "asc" };
			return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
		});
	};

	const updateSelection = (nextSelection) => {
		if (!Array.isArray(rowSelectionModel)) setInternalSelection(nextSelection);
		if (onSelect) onSelect(nextSelection);
	};

	const handleToggleRow = (rowId) => {
		setExpandedRows((prev) => {
			const exists = prev.includes(rowId);
			if (exists) return prev.filter((id) => id !== rowId);
			if (allowMultipleExpand) return [...prev, rowId];
			return [rowId];
		});
	};

	const handleToggleSelectAll = (checked) => {
		if (!checked) {
			updateSelection([]);
			return;
		}
		const allIds = sortedData.map((row) => getRowId(row));
		updateSelection(allIds);
	};

	const handleToggleSelectRow = (checked, rowId) => {
		const set = new Set(currentSelection);
		if (checked) set.add(rowId);
		else set.delete(rowId);
		updateSelection(Array.from(set));
	};

	const allVisibleSelected =
		sortedData.length > 0 && sortedData.every((row) => currentSelection.includes(getRowId(row)));
	const someVisibleSelected =
		sortedData.some((row) => currentSelection.includes(getRowId(row))) && !allVisibleSelected;

	const handleChangePage = (_, newPage) => {
		setPage(newPage);
		if (typeof onPageChange === "function") onPageChange(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		const nextSize = parseInt(event.target.value, 10);
		setRowsPerPage(nextSize);
		setPage(0);
	};

	const getFilterOptionsForField = (field) => {
		if (!field) return [];
		const rowsWithOtherFilters = applyColumnFilters(
			searchedData || [],
			Object.entries(columnFilters || {}).reduce((acc, [key, values]) => {
				if (key === field) return acc;
				acc[key] = values;
				return acc;
			}, {})
		);
		const options = new Set(
			(rowsWithOtherFilters || []).map((row) => getRowFilterText(row, field))
		);
		return Array.from(options).sort((a, b) =>
			String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
		);
	};

	const activeFilterOptions = useMemo(() => getFilterOptionsForField(activeFilterField), [activeFilterField, searchedData, columnFilters, enhancedColumns]);
	const searchedActiveFilterOptions = useMemo(() => {
		const query = (filterOptionSearch || "").toLowerCase().trim();
		if (!query) return activeFilterOptions;
		return activeFilterOptions.filter((option) => String(option).toLowerCase().includes(query));
	}, [activeFilterOptions, filterOptionSearch]);

	const handleOpenFilter = (event, field) => {
		event.stopPropagation();
		setFilterAnchorEl(event.currentTarget);
		setActiveFilterField(field);
		setFilterOptionSearch("");
	};

	const handleCloseFilter = () => {
		setFilterAnchorEl(null);
		setActiveFilterField(null);
		setFilterOptionSearch("");
	};

	const handleSetColumnFilterValues = (field, nextValues, allOptions, { keepEmptySelection = false } = {}) => {
		setColumnFilters((prev) => {
			const normalized = Array.isArray(nextValues) ? nextValues : [];
			const allOptionCount = allOptions?.length || 0;
			const shouldResetToUnfiltered = allOptionCount > 0 && normalized.length === allOptionCount;

			if (shouldResetToUnfiltered) {
				if (!(field in prev)) return prev;
				const next = { ...prev };
				delete next[field];
				return next;
			}
			if (normalized.length === 0) {
				if (!keepEmptySelection && !(field in prev)) return prev;
				return { ...prev, [field]: [] };
			}
			return { ...prev, [field]: normalized };
		});
	};

	const isFilterActive = (field) => Array.isArray(columnFilters?.[field]);

	const getSelectedValuesForField = (field, options) => {
		const selected = columnFilters?.[field];
		if (!Array.isArray(selected)) return options;
		return selected;
	};

	const handleToggleFilterOption = (field, option) => {
		const options = getFilterOptionsForField(field);
		const selected = getSelectedValuesForField(field, options);
		const exists = selected.includes(option);
		const next = exists ? selected.filter((value) => value !== option) : [...selected, option];
		handleSetColumnFilterValues(field, next, options, { keepEmptySelection: true });
	};

	const handleSelectAllFilterOptions = (field) => {
		handleSetColumnFilterValues(field, getFilterOptionsForField(field), getFilterOptionsForField(field));
	};

	const handleClearFilterOptions = (field) => {
		handleSetColumnFilterValues(field, [], getFilterOptionsForField(field), { keepEmptySelection: true });
	};

	const getColumnFlex = (col) => {
		const flex = Number(col?.flex);
		if (!Number.isFinite(flex) || flex <= 0) return null;
		return flex;
	};

	const renderCell = (row, col) => {
		const params = getCellValueParams(row, col);
		const value = getResolvedValue(row, col);
		const renderParams = { ...params, value };

		if (typeof col.renderCell === "function") return col.renderCell(renderParams);

		const formatted = callValueFormatterSafely(col, renderParams, value);
		if (formatted != null) return formatted;
		return value == null ? "" : String(value);
	};

	const getColumnWidth = (col) => {
		if (!col?.field) return null;
		if (Number.isFinite(Number(columnWidths[col.field]))) return Number(columnWidths[col.field]);
		if (Number.isFinite(Number(col.width))) return Number(col.width);
		if (Number.isFinite(Number(computedFlexWidths[col.field]))) return Number(computedFlexWidths[col.field]);
		return null;
	};

	const getColumnWidthStyles = (col) => {
		const width = getColumnWidth(col);
		if (!Number.isFinite(width)) return {};
		return {
			width: `${width}px`,
			minWidth: `${width}px`,
			maxWidth: `${width}px`,
		};
	};

	const computedFlexWidths = useMemo(() => {
		const flexColumns = (enhancedColumns || []).filter(
			(col) =>
				col?.field &&
				!Number.isFinite(Number(columnWidths[col.field])) &&
				!Number.isFinite(Number(col.width)) &&
				Number.isFinite(Number(getColumnFlex(col)))
		);
		if (!flexColumns.length) return {};
		if (!Number.isFinite(tableContainerWidth) || tableContainerWidth <= 0) return {};

		const fixedColumnsWidth = (enhancedColumns || []).reduce((sum, col) => {
			if (!col?.field) return sum;
			const resizedWidth = Number(columnWidths[col.field]);
			if (Number.isFinite(resizedWidth)) return sum + resizedWidth;
			const definedWidth = Number(col.width);
			if (Number.isFinite(definedWidth)) return sum + definedWidth;
			return sum;
		}, 0);
		const structuralWidth =
			(hasMasterDetails ? EXPAND_COLUMN_WIDTH : 0) +
			(checkboxSelection ? CHECKBOX_COLUMN_WIDTH : 0);
		const remainingWidth = tableContainerWidth - structuralWidth - fixedColumnsWidth;
		const totalFlex = flexColumns.reduce((sum, col) => sum + Number(getColumnFlex(col)), 0);
		if (!Number.isFinite(totalFlex) || totalFlex <= 0) return {};

		const next = {};
		flexColumns.forEach((col) => {
			const ratio = Number(getColumnFlex(col)) / totalFlex;
			const raw = remainingWidth * ratio;
			next[col.field] = Math.max(MIN_COLUMN_WIDTH, Math.round(raw));
		});
		return next;
	}, [enhancedColumns, columnWidths, tableContainerWidth, hasMasterDetails, checkboxSelection]);

	const tableWidthMeta = useMemo(() => {
		let totalFixedWidth = hasMasterDetails ? EXPAND_COLUMN_WIDTH : 0;
		if (checkboxSelection) totalFixedWidth += CHECKBOX_COLUMN_WIDTH;
		let allColumnsHaveWidth = true;

		enhancedColumns.forEach((col) => {
			const width = getColumnWidth(col);
			if (Number.isFinite(width)) totalFixedWidth += width;
			else allColumnsHaveWidth = false;
		});

		return { allColumnsHaveWidth, totalFixedWidth };
	}, [enhancedColumns, checkboxSelection, hasMasterDetails, columnWidths, computedFlexWidths]);

	const enableHorizontalScroll =
		tableWidthMeta.allColumnsHaveWidth &&
		tableContainerWidth > 0 &&
		tableWidthMeta.totalFixedWidth > tableContainerWidth + 8;

	const getAlignValue = (value, fallback = "left") => {
		if (value === "left" || value === "center" || value === "right") return value;
		return fallback;
	};

	const getAlignJustifyContent = (value) => {
		if (value === "center") return "center";
		if (value === "right") return "flex-end";
		return "flex-start";
	};

	const handleColumnResizeStart = (event, col) => {
		if (!col?.field) return;
		event.preventDefault();
		event.stopPropagation();

		const field = col.field;
		const startX = event.clientX;
		const baseWidth =
			getColumnWidth(col) ||
			headerCellRefs.current[field]?.offsetWidth ||
			150;

		const prevUserSelect = document.body.style.userSelect;
		document.body.style.userSelect = "none";
		setActiveResizeField(field);

		const onMouseMove = (moveEvent) => {
			const delta = moveEvent.clientX - startX;
			const nextWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(baseWidth + delta));
			setColumnWidths((prev) => {
				if (prev[field] === nextWidth) return prev;
				return { ...prev, [field]: nextWidth };
			});
		};

		const onMouseUp = () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			document.body.style.userSelect = prevUserSelect;
			setActiveResizeField(null);
		};

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
	};

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				height: "fit-content",
				width: "100%",
				marginBottom: "30px",
			}}
		>
			{showSearchBar && (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "5px",
					}}
				>
					<TextField
						variant="outlined"
						size="small"
						value={searchText}
						onChange={(e) => setSearchText((e.target.value || "").toLowerCase())}
						placeholder={t("PLACEHOLDER.SEARCH")}
						style={{ marginRight: "20px", minWidth: "20%" }}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>

					<Toolbar style={{ display: "flex", gap: "8px", margin: 0, padding: 0 }} data-tour="admin-user-actions">
						{customButtons &&
							customButtons.map((button, index) => (
								<Button
									key={index}
									startIcon={button?.icon}
									variant="contained"
									color="primary"
									onClick={button.onClick}
								>
									{button.label}
									{button?.upload && (
										<input
											type="file"
											hidden
											onChange={button?.handleFileChange}
											accept={button?.acceptedFile}
										/>
									)}
								</Button>
							))}
					</Toolbar>
				</div>
			)}

			<Box
				sx={{
					width: "100%",
					height: autoHeight
						? "auto"
						: typeof gridHeight === "number"
							? `${gridHeight}px`
							: gridHeight || "420px",
					overflowY: autoHeight ? "visible" : "auto",
					overflowX: "hidden",
					borderRadius: "10px",
					border: "1px solid",
					borderColor: "divider",
					backgroundColor: (theme) => theme.palette.background.paper,
					"& .MuiTablePagination-selectLabel": { marginBottom: 0 },
					"& .MuiTablePagination-displayedRows": { marginBottom: 0 },
				}}
			>
				<TableContainer
					ref={tableContainerRef}
					sx={{
						borderRadius: "10px",
						overflowX: enableHorizontalScroll ? "auto" : "hidden",
						"&::-webkit-scrollbar": {
							width: 6,
							height: 3,
						},
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: "#cfcfcf",
							borderRadius: "8px",
						},
						"&::-webkit-scrollbar-track": {
							backgroundColor: "transparent",
						},
					}}
				>
					<Table
						size="small"
						sx={{
							width: "100%",
							minWidth:
								enableHorizontalScroll && tableWidthMeta.allColumnsHaveWidth
									? `${tableWidthMeta.totalFixedWidth}px`
									: "100%",
							tableLayout: "fixed",
						}}
					>
						<TableHead>
							<TableRow sx={{ height: `${HEADER_FOOTER_HEIGHT}px` }}>
								{hasMasterDetails && <TableCell sx={{ width: 56, py: 0, height: `${HEADER_FOOTER_HEIGHT}px` }} />}
								{checkboxSelection && (
									<TableCell
										padding="checkbox"
										sx={{ py: 0, height: `${HEADER_FOOTER_HEIGHT}px` }}
									>
										{!disableSelectAll && (
											<Checkbox
												checked={allVisibleSelected}
												indeterminate={someVisibleSelected}
												onChange={(e) => handleToggleSelectAll(e.target.checked)}
											/>
										)}
									</TableCell>
								)}
								{enhancedColumns.map((col, colIndex) => {
									const headerAlign = getAlignValue(col.headerAlign, getAlignValue(col.align, "left"));
									const headerJustify = getAlignJustifyContent(headerAlign);
									const sortable = col.sortable !== false;
									const active = sortState.field === col.field;
									const isLastColumn = colIndex === enhancedColumns.length - 1;
									const filterable = isColumnFilterable(col);
									const filterActive = isFilterActive(col.field);
									return (
										<TableCell
											key={col.field || col.headerName}
											align={headerAlign}
											sx={{
												...getColumnWidthStyles(col),
												fontWeight: 500,
												color: "text.primary",
												backgroundColor: (theme) => theme.palette.background.paper,
												py: 0,
												height: `${HEADER_FOOTER_HEIGHT}px`,
												boxSizing: "border-box",
												position: "relative",
												whiteSpace: "nowrap",
												"& .MuiTableSortLabel-root": {
													fontWeight: 500,
													display: "flex",
													maxWidth: "100%",
												},
												"& .MuiTableSortLabel-icon": {
													fontSize: "1rem",
													flexShrink: 0,
												},
												"& .MuiTableSortLabel-root .MuiTableSortLabel-icon": {
													marginLeft: "4px",
												},
												"& .MuiTableSortLabel-root .MuiTableSortLabel-label": {
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												},
											}}
											ref={(el) => {
												if (col?.field) headerCellRefs.current[col.field] = el;
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5, pr: 1.5, minWidth: 0 }}>
												{sortable ? (
													<TableSortLabel
														active={active}
														direction={active ? sortState.direction : "asc"}
														onClick={() => handleSortClick(col.field, sortable)}
														sx={{ minWidth: 0, flex: 1, justifyContent: headerJustify, textAlign: headerAlign }}
													>
														{col.headerName}
													</TableSortLabel>
												) : (
													<Box
														component="span"
														sx={{
															minWidth: 0,
															flex: 1,
															textAlign: headerAlign,
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
													>
														{col.headerName}
													</Box>
												)}
												{filterable && (
													<IconButton
														size="small"
														onClick={(event) => handleOpenFilter(event, col.field)}
														sx={{
															ml: "auto",
															color: filterActive ? "primary.main" : "text.secondary",
														}}
													>
														<FilterListIcon fontSize="inherit" />
													</IconButton>
												)}
											</Box>
											{!isLastColumn && (
												<Box
													onMouseDown={(e) => handleColumnResizeStart(e, col)}
													sx={{
														position: "absolute",
														top: 0,
														right: 0,
														width: 12,
														height: "100%",
														cursor: "col-resize",
														zIndex: 3,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														backgroundColor:
															activeResizeField === col.field ? "action.hover" : "transparent",
														"&::after": {
															content: "\"\"",
															width: "1px",
															height: "38%",
															backgroundColor:
																activeResizeField === col.field ? "#f1f1f1" : "#f1f1f1",
														},
														"&:hover::after": {
															backgroundColor: "#eee",
														},
													}}
												/>
											)}
										</TableCell>
									);
								})}
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedRows.map((row, rowIndex) => {
								const rowId = getRowId(row);
								const isExpanded = expandedRows.includes(rowId);
								const isSelected = currentSelection.includes(rowId);
								const isLastDataRow = rowIndex === paginatedRows.length - 1;
								return (
									<React.Fragment key={rowId}>
										<TableRow
											hover
											onClick={
												rowClick
													? () => onRowClick?.({ id: rowId, row })
													: undefined
											}
											sx={{
												cursor: rowClick ? "pointer" : "default",
												height: `${HEADER_FOOTER_HEIGHT}px`,
												"& .MuiTableCell-root": {
													backgroundColor: (theme) => theme.palette.background.paper,
													height: `${HEADER_FOOTER_HEIGHT}px`,
													py: 0,
													boxSizing: "border-box",
													...(isLastDataRow && !isExpanded ? { borderBottom: "none" } : {}),
												},
												...(isSelected && {
													"& .MuiTableCell-root": {
														backgroundColor: "action.selected",
													},
												}),
											}}
										>
											{hasMasterDetails && (
												<TableCell sx={{ width: 56 }}>
													<IconButton
														size="small"
														onClick={(e) => {
															e.stopPropagation();
															handleToggleRow(rowId);
														}}
													>
														{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
													</IconButton>
												</TableCell>
											)}
											{checkboxSelection && (
												<TableCell
													padding="checkbox"
													onClick={(e) => e.stopPropagation()}
												>
													<Checkbox
														checked={isSelected}
														onChange={(e) => handleToggleSelectRow(e.target.checked, rowId)}
													/>
												</TableCell>
											)}
											{enhancedColumns.map((col) => (
												<TableCell
													key={`${rowId}-${col.field}`}
													align={getAlignValue(col.align, "left")}
													sx={{
														...getColumnWidthStyles(col),
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}
												>
													{renderCell(row, col)}
												</TableCell>
											))}
										</TableRow>

										{hasMasterDetails && (
											<TableRow
												sx={{
													...(isExpanded
														? {}
														: {
															height: 0,
															"& .MuiTableCell-root": {
																borderBottom: "none",
															},
														}),
												}}
											>
												<TableCell
													colSpan={visibleColumnCount}
													sx={{
														height: "auto",
														py: 0,
														borderBottom: isExpanded
															? (isLastDataRow ? "none" : undefined)
															: "none",
													}}
												>
													<Collapse
														in={isExpanded}
														timeout={220}
														unmountOnExit
														easing={{
															enter: "cubic-bezier(0.4, 0, 0.2, 1)",
															exit: "cubic-bezier(0.4, 0, 0.2, 1)",
														}}
													>
														<Box sx={{ py: 2 }}>
															{masterDetails(row, {
																rowId,
																isExpanded,
																toggleRow: () => handleToggleRow(rowId),
																collapseRow: () => {
																	setExpandedRows((prev) => prev.filter((id) => id !== rowId));
																},
															})}
														</Box>
													</Collapse>
												</TableCell>
											</TableRow>
										)}
									</React.Fragment>
								);
							})}
							{paginatedRows.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={visibleColumnCount}
										align="center"
										sx={{
											height: `${HEADER_FOOTER_HEIGHT * 3}px`,
											color: "text.secondary",
											fontSize: "0.875rem",
											borderBottom: "none",
										}}
									>
										{t("UI_TEXT.NO_DATA_FOUND", { defaultValue: "No data found" })}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={sortedData.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					rowsPerPageOptions={normalizedPageSizeOptions}
					sx={{
						borderTop: "1px solid",
						borderTopColor: "divider",
						backgroundColor: (theme) => theme.palette.background.paper,
						borderBottomLeftRadius: "10px",
						borderBottomRightRadius: "10px",
						"& .MuiTablePagination-toolbar": {
							minHeight: `${HEADER_FOOTER_HEIGHT}px`,
							height: `${HEADER_FOOTER_HEIGHT}px`,
						},
					}}
				/>
			</Box>
			<Popover
				open={Boolean(filterAnchorEl) && Boolean(activeFilterField)}
				anchorEl={filterAnchorEl}
				onClose={handleCloseFilter}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Box sx={{ width: 300, maxWidth: "90vw", p: 1.5 }}>
					<TextField
						size="small"
						fullWidth
						value={filterOptionSearch}
						onChange={(event) => setFilterOptionSearch(event.target.value)}
						placeholder={t("PLACEHOLDER.SEARCH")}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
					<Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
						<Button size="small" onClick={() => handleSelectAllFilterOptions(activeFilterField)}>
							{t("BUTTON.SELECT_ALL", { defaultValue: "Select all" })}
						</Button>
						<Button size="small" onClick={() => handleClearFilterOptions(activeFilterField)}>
							{t("BUTTON.CLEAR", { defaultValue: "Clear" })}
						</Button>
					</Box>
					<Divider sx={{ my: 1 }} />
					<List dense sx={{ maxHeight: 280, overflowY: "auto", py: 0 }}>
						{searchedActiveFilterOptions.map((option) => {
							const selectedValues = getSelectedValuesForField(activeFilterField, activeFilterOptions);
							const checked = selectedValues.includes(option);
							return (
								<ListItemButton
									key={`${activeFilterField}-${option}`}
									onClick={() => handleToggleFilterOption(activeFilterField, option)}
									sx={{ py: 0.5 }}
								>
									<ListItemIcon sx={{ minWidth: 34 }}>
										<Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
									</ListItemIcon>
									<ListItemText
										primary={option || "—"}
										primaryTypographyProps={{ variant: "body2", noWrap: true }}
									/>
								</ListItemButton>
							);
						})}
						{searchedActiveFilterOptions.length === 0 && (
							<ListItemText
								primary={t("UI_TEXT.NO_DATA_FOUND", { defaultValue: "No data found" })}
								primaryTypographyProps={{ variant: "body2", color: "text.secondary", sx: { px: 1.5, py: 1 } }}
							/>
						)}
					</List>
				</Box>
			</Popover>
		</div>
	);
};

export default ExpandableDataGrid;
